'use client'

import { Button } from '@/General/Button'
import { useForm } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useGiftCertificateOptionsQuery } from '@/generated/graphql'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useAddItemToCart } from '@/useAddItemToCart'
import { useStorefrontMarket } from '@/useStorefrontMarket'

type GiftCertificateFormData = {
  recipientName: string
  amount: string
}

export const PurchaseForm = () => {
  const storefrontMarket = useStorefrontMarket()
  const queryClient = useQueryClient()
  const router = useRouter()

  const { data } = useGiftCertificateOptionsQuery({
    currencyId: storefrontMarket.currencyId,
  })

  const { addItem: addToCart, isPending } = useAddItemToCart({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ActiveShoppingCart'],
        exact: false,
      })
      router.push('/checkout')
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GiftCertificateFormData>({
    defaultValues: {
      recipientName: '',
      amount: '',
    },
  })

  const onSubmit = (data: GiftCertificateFormData) => {
    addToCart({
      productId: 1161, // Gift certificate product ID
      productOptionId: parseInt(data.amount),
      description: data.recipientName,
    })
  }

  return (
    <div className="w-full md:w-1/2 my-6 md:my-0">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-gray-200 p-6 rounded-lg shadow-sm"
      >
        <ErrorBox
          errors={
            Object.values(errors)
              .map(error => error?.message)
              .filter(Boolean) as string[]
          }
        />

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block font-medium text-gray-700">
              Recipient's Name
            </label>
            <input
              type="text"
              className={`w-full p-2 border rounded-md shadow-sm focus:outline-none ${errors.recipientName ? 'border-red-500' : 'border-gray-300'}`}
              {...register('recipientName', {
                required: 'Recipient name is required',
              })}
            />
          </div>

          <div className="space-y-2">
            <label className="block font-medium text-gray-700">
              Amount to Gift
            </label>
            <select
              className={`w-full p-2 border rounded-md shadow-sm focus:outline-none ${errors.amount ? 'border-red-500' : 'border-gray-300'}`}
              {...register('amount', { required: 'Please select an amount' })}
            >
              <option value="">Select an amount</option>
              {data?.giftCertificateOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.currencySymbol}
                  {option.price.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          <Button
            type="submit"
            color="green"
            className="w-full"
            disabled={isPending}
          >
            {isPending ? 'Adding to Cart...' : 'Purchase Gift Certificate'}
          </Button>
        </div>
      </form>
    </div>
  )
}
